/**
 * Headless Playlist Utility Functions
 *
 * Pure utility functions for mode detection, boolean conversion, and section ID generation.
 * Safe to import from server and client components.
 */

export type HeadlessPlaylistType = "horizontal" | "vertical" | "tabs" | "disclaimer";
export type HeadlessPlaylistLayout = "scroll" | "list" | "grid" | "tabs";
export type HeadlessBoolean = boolean | "true" | "false";

// Re-declared here to avoid importing from the client bundle.
// Must stay in sync with MediaSectionMode in UnifiedMediaSection.
export type HeadlessPlaylistRenderMode = "live" | "previous" | "upcoming" | "playlist" | "category";

export interface HeadlessPlaylistModeProps {
  readonly type: HeadlessPlaylistType;
  readonly title: string;
  readonly sectionId?: string;
  readonly sectionMode?: HeadlessPlaylistRenderMode;
  readonly remindMeText?: string;
  readonly isLiveNudge?: HeadlessBoolean;
  readonly isTimestamp?: HeadlessBoolean;
  readonly layoutTypeMobile?: HeadlessPlaylistLayout;
}

export function toBoolean(value?: HeadlessBoolean): boolean {
  return value === true || value === "true";
}

/** CleverTap section label: prefer AEM `title_ct` (English), then display `title`. */
export function getCtSectionTitle(titleCt?: string, title?: string, fallback = "NA"): string {
  if (typeof titleCt === "string" && titleCt.trim()) return titleCt.trim();
  if (typeof title === "string" && title.trim()) return title.trim();
  return fallback;
}

function slugifyTitle(title: string): string {
  const lower = title.trim().toLowerCase();
  let result = "";
  let prevWasHyphen = true; // suppress leading hyphens

  for (const ch of lower) {
    if ((ch >= "a" && ch <= "z") || (ch >= "0" && ch <= "9")) {
      result += ch;
      prevWasHyphen = false;
    } else if (!prevWasHyphen) {
      result += "-";
      prevWasHyphen = true;
    }
  }

  // Remove trailing hyphen
  return result.endsWith("-") ? result.slice(0, -1) : result;
}

export function getDefaultHeadlessSectionId(title: string): string {
  const normalizedTitle = title.trim().toLowerCase();

  if (normalizedTitle.includes("trending now")) return "trending-now-title";
  if (normalizedTitle.includes("blockbuster")) return "blockbuster-heading";
  if (normalizedTitle.includes("upcoming")) return "upcoming-live-title";
  if (normalizedTitle.includes("shop on emi")) return "shop-on-emi-heading";
  if (normalizedTitle.includes("investment")) return "investments-heading";
  if (normalizedTitle.includes("loan")) return "loans-heading";

  return `${slugifyTitle(title)}-section`;
}

export function resolveHeadlessPlaylistMode(
  props: HeadlessPlaylistModeProps
): HeadlessPlaylistRenderMode {
  if (props.type === "tabs") return "category";

  if (props.sectionMode) return props.sectionMode;

  if (props.sectionId === "other-live-sessions-section") return "live";
  if (props.sectionId === "previously-live-sessions-section") return "previous";
  if (props.sectionId === "upcoming-live-sessions-section") return "upcoming";

  if (props.remindMeText) return "upcoming";
  if (
    props.layoutTypeMobile === "list" &&
    !toBoolean(props.isLiveNudge) &&
    toBoolean(props.isTimestamp)
  ) {
    return "previous";
  }

  return "playlist";
}
